'use client';

import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Group, Text, Title, Box } from '@mantine/core';
import { useMentorSearchStore } from '@/stores/useMentorSearchStore';
import MentorSearchInput from '@/components/MentorSearchInput';
import { useMediaQuery } from '@mantine/hooks';


export default function MentorSearchHero() {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const searchText          = useMentorSearchStore((s) => s.searchText);
  const setSearchText       = useMentorSearchStore((s) => s.setSearchText);
  const openModal           = useMentorSearchStore((s) => s.openModal);

  const users               = useMentorSearchStore((s) => s.users);
  const handleLLMSearch     = useMentorSearchStore((s) => s.handleLLMSearch);
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUserSearch();
    }
  };

  const hasSearched = useMentorSearchStore((s) => s.hasSearched);

  const usersLength = users ? users.length : 0;
  const isLoading = useMentorSearchStore((s) => s.isLoading);
  
  // Track if we've done the initial load to prevent re-triggering on empty results
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = React.useState(false);
  const [userHasSearched, setUserHasSearched] = React.useState(false);
  
  // Track if we've processed homepage query to avoid loops
  const [hasProcessedHomepageQuery, setHasProcessedHomepageQuery] = React.useState(false);

  // Wrap handleLLMSearch to track user-initiated searches
  const handleUserSearch = React.useCallback(() => {
    setUserHasSearched(true);
    setHasInitiallyLoaded(true); // Cancel any pending initial load
    handleLLMSearch();
  }, [handleLLMSearch]);
  
  // Check for homepage query immediately on mount
  useEffect(() => {
    const homeSearchQuery = sessionStorage.getItem('homeSearchQuery');
    if (homeSearchQuery) {
      setSearchText(homeSearchQuery);
      sessionStorage.removeItem('homeSearchQuery');
      setUserHasSearched(true);
      setHasInitiallyLoaded(true);
      setHasProcessedHomepageQuery(true);
      setShouldTriggerHomepageSearch(true);
    } else {
      setHasProcessedHomepageQuery(true);
    }
  }, [setSearchText]);

  // Focus input after processing homepage query
  useEffect(() => {
    if (hasProcessedHomepageQuery) {
      inputRef.current?.focus();
    }
  }, [hasProcessedHomepageQuery]);

  // Only trigger search from homepage, not on manual typing
  const [shouldTriggerHomepageSearch, setShouldTriggerHomepageSearch] = React.useState(false);
  
  useEffect(() => {
    if (shouldTriggerHomepageSearch && searchText && userHasSearched && hasProcessedHomepageQuery) {
      handleLLMSearch();
      setShouldTriggerHomepageSearch(false);
    }
  }, [shouldTriggerHomepageSearch, searchText, userHasSearched, hasProcessedHomepageQuery, handleLLMSearch]);

  // Only do initial load after homepage check is complete
  useEffect(() => {
    if (hasProcessedHomepageQuery && !hasInitiallyLoaded && !userHasSearched && (!users || users.length === 0) && !isLoading && !searchText.trim()) {
      handleLLMSearch();
      setHasInitiallyLoaded(true);
    }
  }, [hasProcessedHomepageQuery, hasInitiallyLoaded, userHasSearched, users, isLoading, searchText, handleLLMSearch]);

  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: hasSearched() ? -40 : 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%', paddingTop: '2rem' }}
    >
      <AnimatePresence>
        {!hasSearched() && (
          <motion.div
            key="title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Title order={1} ta="center" size="h2" mb="md">
              What type of mentoring are you looking for?
            </Title>
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        style={isMobile ? {
          marginLeft: 'calc(-1 * var(--mantine-spacing-md))',
          marginRight: 'calc(-1 * var(--mantine-spacing-md))',
          paddingLeft: 'var(--mantine-spacing-md)',
          paddingRight: 'var(--mantine-spacing-md)',
        } : {}}
      >
        <MentorSearchInput
          ref={inputRef}
          value={searchText}
          onChange={setSearchText}
          onSearch={handleUserSearch}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUserSearch();
            }
          }}
          showButton={true}
          style={{
            fontSize: '18px',
            height: '60px',
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        />
      </Box>

      <Text
        size="sm"
        c="dimmed"
        ta="center"
        mt="xs"
        style={{ cursor: 'pointer' }}
        onClick={openModal}
      >
        Advanced
      </Text>
    </motion.div>
  );
}
